<script setup>
import { ref, watch } from 'vue'
import ProgressIndicator from '../ProgressIndicator/ProgressIndicator.vue'

const props = defineProps({
  catalog: {
    type: Object,
    default: undefined,
  },
  currentCategory: {
    type: Object,
    default: undefined,
  },
  currentStep: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    default: '',
  },
  value: {
    type: String,
    default: undefined,
  },
  hasSubnavigation: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['selectCategoryById', 'openStep'])

const expandedAreas = ref(new Set())

watch(
  () => props.currentStep,
  () => {
    if (props.hasSubnavigation && props.catalog?.areas?.length) {
      expandedAreas.value = new Set([props.catalog.areas[0].id])
    } else {
      expandedAreas.value = new Set()
    }
  },
  { immediate: true },
)

watch(
  () => props.currentCategory,
  (newCategory) => {
    if (!newCategory || !props.catalog?.areas) return
    const area = props.catalog.areas.find((a) =>
      a.categories.some((c) => c.id == newCategory.id),
    )
    if (area && !expandedAreas.value.has(area.id)) {
      expandedAreas.value = new Set([...expandedAreas.value, area.id])
    }
  },
)

function toggleArea(areaId) {
  if (expandedAreas.value.has(areaId)) {
    expandedAreas.value.delete(areaId)
  } else {
    expandedAreas.value.add(areaId)
  }
  // Trigger reactivity since Set mutations aren't tracked automatically
  expandedAreas.value = new Set(expandedAreas.value)
}

function isAreaExpanded(areaId) {
  return expandedAreas.value.has(areaId)
}
</script>
<template>
  <li
    class="AssessmentNavigationStep"
    :class="{
      'AssessmentNavigationStep--active': props.currentStep == props.value,
    }"
    :aria-current="props.currentStep == props.value ? 'step' : undefined"
  >
    <div class="AssessmentNavigationStep-buttons">
      <button
        class="AssessmentNavigationStep-label"
        :class="{ 'AssessmentNavigationStep-label--disabled': disabled }"
        :disabled="disabled"
        @click="
          emit('openStep', {
            name: props.value,
          })
        "
      >
        {{ props.label }}
      </button>

      <button
        v-if="disabled"
        class="AssessmentNavigationStep-info"
        :popovertarget="props.value + '-info'"
        :style="`anchor-name: --info-${props.value}`"
        aria-label="Hinweis"
      >
        <svg class="AssessmentNavigationStep-infoIcon" aria-hidden="true">
          <use href="#help"></use>
        </svg>
      </button>
    </div>

    <span
      v-if="disabled"
      :id="props.value + '-info'"
      popover
      class="AssessmentNavigationStep-infoPopover"
      :style="`position-anchor: --info-${props.value}`"
    >
      Bitte schließen Sie zunächst die vorhergehenden Schritte ab.
    </span>

    <ul
      v-if="currentStep == props.value && hasSubnavigation"
      class="AssessmentNavigationStep-areas"
    >
      <li
        v-for="area in props.catalog.areas"
        :key="area.id"
        class="AssessmentNavigationStep-area"
      >
        <button
          class="AssessmentNavigationStep-areaLabel"
          :aria-expanded="isAreaExpanded(area.id)"
          @click="toggleArea(area.id)"
        >
          <span class="AssessmentNavigationStep-areaLabelText"
            >{{ area.key }} {{ area.title }}</span
          >
          <svg
            class="AssessmentNavigationStep-areaLabelIcon"
            aria-hidden="true"
          >
            <use href="#arrow-down"></use>
          </svg>
          <ProgressIndicator
            :completed="area.progress.completed"
            :total="area.progress.total"
            :percentage="area.progress.percentage"
            class="AssessmentNavigationStep-areaLabelIndicator"
          />
        </button>

        <ul class="AssessmentNavigationStep-categories" role="list">
          <li
            v-for="category in area.categories"
            :key="category.id"
            class="AssessmentNavigationStep-category"
          >
            <button
              class="AssessmentNavigationStep-categoryButton"
              :class="{
                'AssessmentNavigationStep-categoryButton--active':
                  props.currentCategory?.id == category.id,
              }"
              :aria-current="props.currentCategory?.id == category.id"
              @click="
                emit('selectCategoryById', {
                  id: category.id,
                })
              "
            >
              <span class="AssessmentNavigationStep-categoryButtonLabel">
                {{ category.key }} {{ category.title }}
              </span>
              <ProgressIndicator
                :completed="category.progress.completed"
                :total="category.progress.total"
                class="AssessmentNavigationStep-categoryButtonIndicator"
                :percentage="category.progress.percentage"
                :small="true"
              />
            </button>
          </li>
        </ul>
      </li>
    </ul>
  </li>
</template>
